/**
 * Conversion Funnel Component for Admin Control Panel
 * 
 * Phase 2: Visual funnel showing user journey stages with conversion rates.
 */
import React from "react";
import { ArrowRight, ArrowDown, Users, UserPlus, BookOpen, Play, CheckCircle } from "lucide-react";

const ConversionFunnel = ({ data, onStageClick, loading }) => {
  const stages = [
    {
      key: "visitors",
      label: "Site Visitors",
      value: data?.funnel?.site_visitors || 0,
      icon: Users,
      color: "from-slate-600 to-slate-700",
      borderColor: "border-slate-500"
    },
    {
      key: "accounts",
      label: "Accounts Created",
      value: data?.funnel?.accounts_created || 0,
      icon: UserPlus,
      color: "from-blue-600 to-blue-700",
      borderColor: "border-blue-500",
      rate: data?.conversion_rates?.visitor_to_registration,
      rateLabel: "visitor→reg"
    },
    {
      key: "library",
      label: "Library Adds",
      value: data?.funnel?.library_adds || 0,
      icon: BookOpen,
      color: "from-purple-600 to-purple-700",
      borderColor: "border-purple-500",
      rate: data?.conversion_rates?.registration_to_library,
      rateLabel: "reg→library"
    },
    {
      key: "player",
      label: "Opened Player",
      value: data?.funnel?.opened_player || 0,
      icon: Play,
      color: "from-amber-600 to-amber-700",
      borderColor: "border-amber-500",
      rate: data?.conversion_rates?.library_to_player,
      rateLabel: "library→player"
    },
    {
      key: "completed",
      label: "Completed Session",
      value: data?.funnel?.completed_session || 0,
      icon: CheckCircle,
      color: "from-emerald-600 to-emerald-700",
      borderColor: "border-emerald-500",
      rate: data?.conversion_rates?.player_to_completion,
      rateLabel: "player→complete"
    }
  ];

  const maxValue = Math.max(...stages.map(s => s.value), 1);

  const getBarWidth = (value) => {
    const percentage = (value / maxValue) * 100;
    return Math.max(percentage, 15); // Minimum 15% width
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-800 rounded w-48"></div>
          <div className="flex gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex-1 h-24 bg-slate-800 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-6"
      data-testid="conversion-funnel"
    >
      <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
          Conversion Funnel
        </span>
      </h3>

      {/* Desktop/Tablet: Horizontal Funnel */}
      <div className="hidden sm:block">
        <div className="flex items-stretch gap-2">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.key}>
              {/* Stage Box */}
              <div 
                className="flex-1 flex flex-col cursor-pointer group"
                onClick={() => onStageClick(stage.key, stage.label)}
                data-testid={`funnel-stage-${stage.key}`}
              >
                <div 
                  className={`
                    relative rounded-lg border-2 ${stage.borderColor} 
                    bg-gradient-to-b ${stage.color} 
                    p-4 text-center transition-all
                    hover:scale-105 hover:shadow-lg hover:shadow-${stage.key === 'completed' ? 'emerald' : stage.key === 'player' ? 'amber' : 'blue'}-500/20
                  `}
                  style={{ 
                    minHeight: '100px',
                    opacity: 0.7 + (getBarWidth(stage.value) / 100) * 0.3
                  }}
                >
                  <stage.icon className="w-5 h-5 mx-auto text-white/80 mb-2" />
                  <div className="text-xs font-medium text-white/80 uppercase tracking-wide mb-1">
                    {stage.label}
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {formatNumber(stage.value)}
                  </div>
                </div>
                
                {/* Click hint */}
                <div className="text-xs text-center text-slate-500 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to view details
                </div>
              </div>

              {/* Arrow with Conversion Rate */}
              {index < stages.length - 1 && (
                <div className="flex flex-col items-center justify-center px-1">
                  <ArrowRight className="w-5 h-5 text-slate-600" />
                  {stages[index + 1].rate !== null && stages[index + 1].rate !== undefined && (
                    <div className="text-xs text-slate-400 mt-1 text-center">
                      <div className="font-semibold text-blue-400">
                        {stages[index + 1].rate}%
                      </div>
                      <div className="text-slate-500 text-[10px]">
                        {stages[index + 1].rateLabel}
                      </div>
                    </div>
                  )}
                  {(stages[index + 1].rate === null || stages[index + 1].rate === undefined) && (
                    <div className="text-xs text-slate-500 mt-1">—</div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Mobile: Vertical Funnel */}
      <div className="sm:hidden space-y-3">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.key}>
            {/* Stage Box */}
            <div 
              className={`
                relative rounded-lg border-2 ${stage.borderColor} 
                bg-gradient-to-r ${stage.color} 
                p-4 cursor-pointer transition-all
                hover:scale-[1.02] active:scale-[0.98]
              `}
              onClick={() => onStageClick(stage.key, stage.label)}
              data-testid={`funnel-stage-${stage.key}-mobile`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <stage.icon className="w-5 h-5 text-white/80" />
                  <span className="text-sm font-medium text-white/90">
                    {stage.label}
                  </span>
                </div>
                <div className="text-xl font-bold text-white">
                  {formatNumber(stage.value)}
                </div>
              </div>
            </div>

            {/* Arrow with Conversion Rate */}
            {index < stages.length - 1 && (
              <div className="flex items-center justify-center gap-2 py-1">
                <ArrowDown className="w-4 h-4 text-slate-600" />
                {stages[index + 1].rate !== null && stages[index + 1].rate !== undefined && (
                  <span className="text-xs text-blue-400 font-medium">
                    {stages[index + 1].rate}% {stages[index + 1].rateLabel}
                  </span>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-slate-800">
        <p className="text-xs text-slate-500 text-center">
          Click any stage to see detailed breakdown • Percentages show conversion rate from previous stage
        </p>
      </div>
    </div>
  );
};

export default ConversionFunnel;
